import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import parse from 'html-react-parser'
import { m } from 'framer-motion'
import Player from '@vimeo/player'
import { useIsClient } from '@/hooks'

type MediaProps = {
    vimeo: string
    showVimeo: boolean
    setShowVimeo: (value: boolean) => void
}

const CloseButton = ({ setShowVimeo }: Pick<MediaProps, 'setShowVimeo'>) => {
    const styles = {
        common: 'w-[80%] h-[1px] bg-v-white absolute top-[26px] right-[6px] transition-all duration-300'
    }
    return (
        <button
            onClick={() => setShowVimeo(false)}
            className="w-[50px] md:w-[50px] h-[50px] md:h-[50px] absolute top-7 md:top-[2.4vh] right-5 md:right-[1vw] inline-block group z-super"
        >
            <span
                className={clsx(
                    styles.common,
                    'rotate-[-45deg] md:group-hover:!rotate-[0deg]'
                )}
            />
            <span
                className={clsx(
                    styles.common,
                    'rotate-[45deg] md:group-hover:!rotate-[0deg] md:group-hover:w-0'
                )}
            />
        </button>
    )
}

export const VimeoMedia = ({ vimeo, showVimeo, setShowVimeo }: MediaProps) => {
    const [parsedVimeo, setParsedVimeo] = useState<any>(null)
    const isClient = useIsClient()

    // parse vimeo on client side only
    useEffect(() => {
        if (isClient) {
            setParsedVimeo(parse(vimeo))
        }
    }, [vimeo, isClient])

    // play/pause vimeo
    useEffect(() => {
        if (typeof document !== 'undefined') {
            const iframe = document.querySelector('iframe')
            if (iframe) {
                const player = new Player(iframe)
                showVimeo ? null : player.pause()
            }
        }
    }, [showVimeo])

    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!showVimeo || !isClient) return
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setShowVimeo(false)
            }
        }
        if (typeof document !== 'undefined') {
            document.addEventListener('mousedown', handleClickOutside)
            return () =>
                document.removeEventListener('mousedown', handleClickOutside)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showVimeo, ref, isClient])

    return (
        <m.div
            initial={{
                opacity: 0,
                pointerEvents: 'none'
            }}
            animate={{
                opacity: showVimeo ? 1 : 0,
                pointerEvents: showVimeo ? 'auto' : 'none'
            }}
            exit={{
                opacity: 0,
                pointerEvents: 'none'
            }}
            transition={{ duration: 0.5 }}
            className="absolute top-0 left-0 w-full h-full z-super bg-v-black"
        >
            <div className="w-full h-full relative z-modal flex items-center justify-center px-7 py-[calc(1.75rem+50px)] md:p-[calc(1vw+50px)]">
                <div
                    ref={ref}
                    className="w-full h-full [&>div]:!w-full [&>div]:!h-full [&>div]:!object-contain [&>div]:!p-0"
                >
                    {isClient && parsedVimeo}
                </div>
            </div>
            <CloseButton setShowVimeo={setShowVimeo} />
        </m.div>
    )
}
